import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getCareersPage, setCareersPage } from '../../../../network/ats';
import { useCurrentUserRole } from '../../../../hooks/useCurrentUserRole';
import { hasRoleLevel } from '../../../../utils/roles';
import { useToast } from '../../../../utils/useToast';
import { Button } from '../../../../ui/Button';

/**
 * The company's public careers page address. Open, public vacancies are
 * listed there and candidates apply through it. Admins choose the address.
 */
export function CareersPageCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { level } = useCurrentUserRole();
  const isAdmin = hasRoleLevel(level, 'admin');
  const { data, isLoading, isError } = useQuery(
    ['careers-page'],
    getCareersPage,
    { retry: false },
  );
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState('');

  useEffect(() => {
    if (data) setSlug(data.slug ?? data.suggested ?? '');
  }, [data]);

  const save = useMutation(() => setCareersPage(slug.trim().toLowerCase()), {
    onSuccess: () => {
      queryClient.invalidateQueries(['careers-page']);
      setEditing(false);
      toast({ title: 'Saved', description: 'Careers page address updated.', variant: 'success' });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      toast({
        title: 'Not saved',
        description: typeof message === 'string' ? message : 'Could not save the address.',
        variant: 'error',
      });
    },
  });

  if (isLoading || isError || !data) return null;

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-white p-4"
      data-testid="careers-page-card"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">Careers page</p>
        {editing ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">jobs.redstonehr.com/</span>
            <label htmlFor="careers-slug" className="sr-only">
              Careers page address
            </label>
            <input
              id="careers-slug"
              className="rounded-md border-gray-300 text-sm"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              maxLength={64}
            />
          </div>
        ) : data.url ? (
          <a
            href={data.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-blue-700 underline break-all"
          >
            {data.url}
          </a>
        ) : (
          <p className="text-sm text-gray-600">
            {isAdmin
              ? 'Choose an address so candidates can see your open roles and apply.'
              : 'An admin needs to choose the address before candidates can apply.'}
          </p>
        )}
      </div>
      {isAdmin &&
        (editing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!slug.trim() || save.isLoading} onClick={() => save.mutate()}>
              {save.isLoading ? 'Saving…' : 'Save address'}
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {data.url ? 'Change address' : 'Set up careers page'}
          </Button>
        ))}
    </div>
  );
}
